@if (session.authStatus() === 'ready') {
  <!-- ===== Coquille applicative =====
       `<uni-shell>`, `<uni-rail>` et `<uni-subnav>` viennent de `@uniproject-git/ui-angular` :
       ce sont les composants du kit, pas leurs classes CSS recopiées. UniApp et UniConforme
       montent le même trio — la structure du shell est une affaire de design system, et la
       réécrire à la main la ferait diverger à la première évolution du kit. -->
  <uni-shell class="app-shell">
    <!-- Rail (64 px) — identité et compte. L'icône vient du package `@uniproject-git/app-icons`
         (aucun tracé local) : squircle acier + glyphe Columns3Cog, l'identité UniIT. -->
    <uni-rail aria-label="Identité et compte">
      <a class="uni-rail__brand" [routerLink]="home()" aria-label="UniIT — accueil">
        <img src="assets/uniit.svg" alt="" width="30" height="30" />
      </a>

      <!-- Outils du rail — au-dessus des sections, comme dans la démo `parcours-shell` du kit.
           Les deux tuiles n'apparaissent que si elles mènent quelque part : une loupe qui n'ouvre
           aucune recherche, ou un « + » sans action de création, seraient exactement le genre de
           leurre que la critique UX du 10.08 a sanctionné. -->
      <div class="uni-rail__tools">
        @if (searchTarget(); as target) {
          <a
            uni-rail-item
            variant="search"
            [routerLink]="target"
            aria-label="Rechercher un matériel"
            title="Rechercher un matériel"
          >
            <uniit-icon name="search" [size]="18" />
          </a>
        }

        @if (createActions().length > 0) {
          <div class="uni-rail__anchor app-create" #create>
            <button
              type="button"
              uni-rail-item
              variant="new"
              (click)="toggleCreate()"
              [attr.aria-expanded]="createOpen()"
              aria-haspopup="menu"
              aria-label="Créer"
              title="Créer"
            >
              <uniit-icon name="circle-plus" [size]="18" />
            </button>

            @if (createOpen()) {
              <div class="uni-menu app-create__menu" role="menu">
                @for (action of createActions(); track action.path) {
                  <a
                    class="app-create__i"
                    role="menuitem"
                    [routerLink]="action.path"
                    [queryParams]="action.query ?? null"
                    (click)="closeCreate()"
                  >
                    {{ action.label }}
                  </a>
                }
              </div>
            }
          </div>
        }

        <!-- Cloche : elle mène à l'écran de notifications — pas à une file métier — et existe
             pour tous les profils. La pastille `.uni-rail__dot` du kit signale SANS dénombrer ;
             le nombre vit dans l'aria-label. La variante `notif` du kit (tuile teintée +
             compteur) n'existe pas dans ui-angular 0.15.0 installé : à reprendre à la prochaine
             montée décidée. -->
        <a
          uni-rail-item
          routerLink="/notifications"
          routerLinkActive
          #notif="routerLinkActive"
          [active]="notif.isActive"
          [attr.aria-label]="notifLabel()"
          title="Notifications"
        >
          <uniit-icon name="bell" [size]="18" />
          @if (notifCount() > 0) {
            <span class="uni-rail__dot" aria-hidden="true"></span>
          }
        </a>
      </div>

      <!-- Une tuile par section visible du rôle : c'est le premier niveau de la navigation. La
           liste est vide sur un menu court, où le panneau rend tout à plat (`railSections`).

           Ni loupe ni « + » ici, contrairement à la démo du Design System : UniIT n'a ni recherche
           globale ni création transverse. Le jour où l'une arrive, sa place est en haut du rail,
           au-dessus de ce groupe, dans un `.uni-rail__tools`.
           `uni-rail-item` est le composant du kit (`.uni-rail__t`), `.uni-rail__grp` son conteneur,
           qui porte l'espacement entre tuiles. La tuile mène à la PREMIÈRE entrée de sa section —
           une tuile qui ouvrirait le panneau sans rien sélectionner laisserait l'utilisateur devant
           un écran inchangé, sans comprendre qu'il s'est passé quelque chose. -->
      <div class="uni-rail__grp">
        @for (group of railSections(); track group.section) {
          <a
            uni-rail-item
            [active]="group.section === activeSection()"
            [routerLink]="group.entries[0].path"
            [attr.aria-label]="group.section"
            [attr.aria-current]="group.section === activeSection() ? 'page' : null"
            [title]="group.section"
          >
            <uniit-icon [name]="sectionIcons[group.section]" [size]="18" />
          </a>
        }
      </div>

      <div class="uni-rail__sp"></div>

      <div class="uni-rail__anchor app-account" #account>
        <!-- Avatar → menu de compte : identité seule (nom + rôle), façon UniApp `.uk-account`. Le
             sélecteur de profil, lui, est le devbar en haut du contenu — deux choses distinctes. -->
        <button
          type="button"
          class="uni-rail__av"
          (click)="toggleAccount()"
          [attr.aria-expanded]="accountOpen()"
          aria-haspopup="menu"
          aria-label="Mon compte"
        >
          {{ session.initials() }}
        </button>
        @if (accountOpen()) {
          <div class="uni-menu app-account__menu" role="menu">
            <div class="app-account__hd">
              <span class="app-account__name">{{ session.displayName() }}</span>
              <span class="app-account__role">{{ session.currentRole().label }}</span>
            </div>
          </div>
        }
      </div>
    </uni-rail>

    <!-- Sous-navigation (224 px) — les entrées de la SEULE section ouverte. Le repli est porté par
         le kit (`[(collapsed)]`), en-tête et chevron compris : rien à redessiner ici. L'en-tête
         prend le nom de la section, ce qui dit quelle tuile du rail est active même replié.

         Les intertitres `.uni-subnav__sec` ont disparu avec le passage au rail : ils empilaient les
         cinq sections dans un panneau de 224 px, ce qui tronquait les libellés, et le Design System
         documente cette variante comme « HORS CIBLE, opt-in ». -->
    <uni-subnav
      [heading]="subnavHeading()"
      [(collapsed)]="railCollapsed"
      aria-label="Navigation de la section"
    >
      @for (entry of subnavEntries(); track entry.id) {
          <a
            uni-subnav-item
            [label]="entry.label"
            [class.app-subnav__i--soon]="!entry.available"
            [routerLink]="entry.path"
            [active]="entry.path === activeEntry()?.path"
            [attr.aria-current]="entry.path === activeEntry()?.path ? 'page' : null"
            [title]="entry.available ? entry.label : entry.label + ' — disponible dans une prochaine version'"
          >
            <uniit-icon [name]="entry.icon" [size]="16" />
            <!-- `trailing` est le mécanisme du kit pour poser une pastille APRÈS le libellé :
                 `<uni-subnav-item>` rend son `label` lui-même, donc tout ce qu'on projette arrive
                 avant lui. Sans cet attribut, la ligne se lit « 8 Collaborateurs ». -->
            @if (!entry.available) {
              <!-- Une entrée « à venir » doit le dire avant le clic, pas après : sans ce signal,
                   l'utilisateur conclut à une app cassée. -->
              <uni-badge trailing tone="grey" size="sm">Bientôt</uni-badge>
            } @else if (entry.count !== null) {
              <uni-badge trailing tone="blue" size="sm">{{ entry.count }}</uni-badge>
          }
        </a>
      }

      <div class="uni-subnav__sp"></div>

      <!-- Bandeau d'état de la source de données : « démonstration » (jeu maquette) ou « API
           connectée » (mode branché — le shell ne s'affiche qu'une fois /api/me résolu). -->
      <div class="app-subnav__foot">
        @if (demoMode) {
          <span class="uni-badge uni-badge--orange">Données de démonstration</span>
        } @else {
          <span class="app-apibadge">
            <span class="app-apibadge__dot" aria-hidden="true"></span>
            API connectée
          </span>
        }
      </div>
    </uni-subnav>

    <!-- ===== Zone principale ===== -->
    <div class="uni-main">
      @if (showDevbar()) {
        <!-- Devbar (façon UniApp/UniScrapp) : bascule de profil de test en haut du contenu. En démo,
             les rôles de la maquette ; en branché (DevBypass), les profils appliqués au VRAI backend
             (droits serveur inclus). Absent en recette/production réelle. -->
        <div class="app-devbar">
          <label class="app-devbar__lab" for="app-devbar-select">Profil de test</label>
          <label class="uni-field uni-field--select app-devbar__field">
            <span class="uni-field__ctl">
              <select id="app-devbar-select" (change)="pickProfile($any($event.target).value)">
                @for (opt of profileOptions(); track opt.key) {
                  <!-- `selected` par option (et non `[value]` sur le select) : sinon le select ne
                       reflète pas le profil courant au premier rendu et retombe sur la 1re option. -->
                  <option [value]="opt.key" [selected]="opt.key === currentProfile()">
                    {{ opt.label }}
                  </option>
                }
              </select>
            </span>
          </label>
        </div>
      }
      <main class="app-content">
        <router-outlet />
      </main>
    </div>
  </uni-shell>
} @else {
  <uniit-session-gate />
}

<!-- Pile de toasts du design system (position fixe), montée à la racine et rendue quel que soit
     l'état d'authentification. Alimentée par `ToastStore` → `UniToasterService`. -->
<uni-toaster />
